import React, { useState } from "react";
import { toast } from "sonner";
import {
  Calendar,
  Users,
  Stethoscope,
  Wallet,
  Boxes,
  Truck,
  UserCog,
  BarChart3,
  Bell,
  RefreshCw,
  ShieldCheck,
  Eye,
  Clock,
  TrendingUp,
  UserPlus,
  Sparkles,
  Loader2,
  CheckCircle2,
} from "lucide-react";

const CAPABILITIES = [
  { icon: Calendar, title: "Appointments & Calendar", desc: "Automated scheduling, reminders & follow-ups. No manual calls." },
  { icon: Users, title: "Patient Management", desc: "Patient records, history, recalls & communications — all automated." },
  { icon: Stethoscope, title: "Treatments Management", desc: "Digital treatment plans, approvals & tracking made simple." },
  { icon: Wallet, title: "Billing & Invoicing", desc: "Automated invoices, payments, GST billing & due reminders." },
  { icon: Boxes, title: "Inventory Management", desc: "AI-powered stock tracking, low-stock alerts & auto-reorders." },
  { icon: Truck, title: "Direct Access to Dyntra Store", desc: "Order materials, supplies & equipment directly from Dyntra Store." },
  { icon: UserCog, title: "Staff Management", desc: "Roster, attendance, performance & payroll — all in one place." },
  { icon: BarChart3, title: "Reports & Analytics", desc: "Real-time reports & AI insights to grow your clinic." },
];

const AUTOMATIONS = [
  { icon: Bell, title: "Low Stock Alerts", desc: "AI predicts & alerts before you run out." },
  { icon: RefreshCw, title: "Auto Reorder", desc: "Smart suggestions & one-click reordering." },
  { icon: Clock, title: "Expiry Management", desc: "Never miss expiry — Al tracks everything." },
  { icon: Eye, title: "Complete Visibility", desc: "Track every item, every branch, anytime, anywhere." },
];

const WHY = [
  { icon: Clock, title: "Save Time", desc: "Automate repetitive tasks and focus on patients." },
  { icon: TrendingUp, title: "Increase Productivity", desc: "Automate workflows and empower your team." },
  { icon: UserPlus, title: "Better Patient Experience", desc: "Timely reminders, follow-ups & personalized care." },
  { icon: BarChart3, title: "Increase Revenue", desc: "Identify opportunities and improve clinic profitability." },
  { icon: Bell, title: "Zero Manual Follow-ups", desc: "Automate reminders, recalls & patient communications." },
  { icon: ShieldCheck, title: "Secure & Compliant", desc: "Your data is safe with cloud backup & role-based access." },
  { icon: Eye, title: "Access Anywhere", desc: "Manage your clinic from any device, anytime." },
  { icon: Sparkles, title: "AI Business Insights", desc: "Smart insights and predictions to grow your clinic." },
];

const CAPABILITY_LIST = [
  "Real-time stock tracking",
  "AI-based low stock alerts",
  "Smart reorder suggestions",
  "Expiry tracking & batch management",
  "Direct access to Dyntra Store",
  "Zero manual inventory handling",
];

const initial = {
  full_name: "",
  mobile: "",
  clinic: "",
  email: "",
  city: "",
  chairs: "",
};

export default function ClinicSetup() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.mobile.trim() || !form.email.trim()) {
      toast.error("Please provide name, mobile and email.");
      return;
    }
    setStatus("loading");
    try {
      try {
        await fetch("/api/clinic-support/requests", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
      } catch {}
      const list = JSON.parse(
        localStorage.getItem("dyntra_dyntra_connect_v1") || "[]"
      );
      list.push({ ...form, submitted_at: new Date().toISOString() });
      localStorage.setItem("dyntra_dyntra_connect_v1", JSON.stringify(list));
      setStatus("success");
      toast.success("Request received — our team will call you shortly.");
      setForm(initial);
    } catch {
      setStatus("idle");
      toast.error("Could not submit. Please try again.");
    }
  };

  return (
    <main data-testid="dyntra-connect-page" className="bg-white">
      {/* Hero */}
      <section className="dyntra-container pt-8 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
          <div>
            <span className="inline-flex items-center rounded-full bg-[#F3EFFC] text-[#4B2AAD] text-[10px] font-bold px-3 py-1 uppercase tracking-wider">
              Dyntra Connect
            </span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">
              Run Your Clinic.
              <br />
              <span className="text-[#4B2AAD]">Smarter. Faster.</span>
              <br />
              All in One Place.
            </h1>
            <p className="text-sm text-gray-600 mt-4 max-w-md">
              AI-powered platform that automates entire clinic operations including
              appointments, patient follow-ups, billing, inventory, staff, and
              business growth.
            </p>
            <div className="mt-5 rounded-2xl bg-[#F3EFFC] border-l-4 border-[#4B2AAD] px-4 py-3 max-w-md">
              <p className="text-sm font-semibold text-gray-900">
                Clinics Inventory Management, Everything Automated In.
              </p>
              <ul className="mt-2 space-y-1 text-xs text-gray-600">
                <li>• No manual follow-ups. Everything automated.</li>
                <li>• Smart inventory that tracks, alerts & reorders.</li>
                <li>• AI insights to grow your clinic effortlessly.</li>
              </ul>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#request-demo" className="dyntra-btn-primary">
                Request a Free Demo
              </a>
              <button className="dyntra-btn-ghost">Explore Features</button>
            </div>
          </div>
          <div className="rounded-3xl bg-gradient-to-br from-[#4B2AAD] to-[#6B4CD9] p-6 text-white relative overflow-hidden min-h-[320px]">
            <div className="rounded-2xl bg-white/10 border border-white/30 p-4 backdrop-blur-sm">
              <div className="flex items-center gap-2 text-xs font-semibold">
                <div className="h-6 w-6 rounded-lg bg-white/20 flex items-center justify-center">
                  <BarChart3 className="h-3.5 w-3.5" />
                </div>
                Dyntra Connect
              </div>
              <p className="text-lg font-display font-bold mt-3">Good Morning, Dr. Rohit</p>
              <p className="text-[11px] opacity-80">
                Here&apos;s what&apos;s happening at your clinic today.
              </p>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <MetricPill label="Today's Appointments" value="18" up="+8%" />
                <MetricPill label="Today's Revenue" value="₹54,320" up="+15%" />
                <MetricPill label="Pending Payments" value="₹21,450" />
                <MetricPill label="New Patients" value="12" up="+3%" />
              </div>
              <div className="mt-4 rounded-xl bg-white/10 p-3">
                <p className="text-[11px] font-semibold">Revenue Overview · This Month</p>
                <div className="mt-2 flex items-end gap-1 h-16">
                  {[30, 40, 55, 45, 60, 75, 68, 82, 78, 90, 88, 100].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-t bg-white/70"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Franchise strip */}
      <section className="dyntra-container pb-10">
        <div className="rounded-3xl bg-gradient-to-r from-[#F3EFFC] via-white to-[#F3EFFC] border border-[#4B2AAD]/15 p-6 grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr] items-center gap-4">
          <div>
            <span className="inline-flex items-center rounded-full bg-[#4B2AAD] text-white text-[10px] font-bold px-3 py-1 uppercase tracking-wider">
              Exclusive for Dyntra Clinics Partners
            </span>
            <h3 className="mt-2 font-display text-2xl font-extrabold text-[#4B2AAD]">
              Get Dyntra Connect — Absolutely FREE!
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              100% FREE with Dyntra Clinics Franchise Model
            </p>
            <p className="text-[11px] text-gray-500 mt-2 max-w-md">
              As a Dyntra Clinics franchise partner, you get complete access to
              Dyntra Connect with zero setup cost and zero monthly fees.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <FeeCard label="Setup Fee" value="₹0" />
            <FeeCard label="Monthly Fee" value="₹0" />
            <FeeCard label="FREE" value="100%" />
          </div>
          <button className="dyntra-btn-primary self-center whitespace-nowrap">
            Exclusive for<br />Dyntra Clinics<br />Franchise Partners
          </button>
        </div>
      </section>

      {/* Capabilities */}
      <section className="dyntra-container pb-10">
        <div className="text-center mb-6">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
            Powerful Capabilities. Complete Automation.
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Everything your clinic needs — automated, integrated, and intelligent.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {CAPABILITIES.map((c) => {
            const Ico = c.icon;
            return (
              <div
                key={c.title}
                className="rounded-2xl border border-gray-100 bg-white p-4"
              >
                <div className="h-10 w-10 rounded-xl bg-[#F3EFFC] flex items-center justify-center">
                  <Ico className="h-5 w-5 text-[#4B2AAD]" />
                </div>
                <p className="mt-3 font-display text-sm font-bold text-gray-900">
                  {c.title}
                </p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  {c.desc}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Automation showcase */}
      <section className="dyntra-container pb-10">
        <div className="rounded-3xl bg-[#F5F3FB] p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr_1fr] gap-6 items-center">
          <div>
            <h3 className="font-display text-xl font-extrabold text-gray-900">
              Clinics Inventory Management, Everything Automated In.
            </h3>
            <p className="text-xs text-gray-600 mt-2">
              Dyntra Connect takes care of your entire inventory — from stock
              tracking, to intelligent reordering. No manual work, no stock-outs,
              no overstocking.
            </p>
            <ul className="mt-4 space-y-2 text-xs text-gray-700">
              {CAPABILITY_LIST.map((c) => (
                <li key={c} className="flex items-start gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#4B2AAD] mt-0.5" />
                  {c}
                </li>
              ))}
            </ul>
            <button className="mt-4 dyntra-btn-primary">Learn More →</button>
          </div>
          <div className="rounded-2xl bg-white border border-gray-100 p-4 shadow-inner">
            <div className="rounded-xl bg-[#111827] text-white p-4 aspect-video flex items-center justify-center">
              <div className="text-center opacity-80">
                <BarChart3 className="h-10 w-10 mx-auto text-[#4B2AAD]" />
                <p className="mt-2 text-sm font-semibold">
                  Inventory Dashboard Preview
                </p>
                <p className="text-[10px] opacity-70">
                  6 categories · 24 SKUs · real-time
                </p>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            {AUTOMATIONS.map((a) => {
              const Ico = a.icon;
              return (
                <div
                  key={a.title}
                  className="rounded-2xl bg-white border border-gray-100 p-4 flex items-start gap-3"
                >
                  <div className="h-9 w-9 rounded-full bg-[#F3EFFC] flex items-center justify-center shrink-0">
                    <Ico className="h-4 w-4 text-[#4B2AAD]" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{a.title}</p>
                    <p className="text-[11px] text-gray-500">{a.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Clinics Love */}
      <section className="dyntra-container pb-10">
        <div className="text-center mb-6">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
            Why Clinics Love Dyntra Connect
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            AI-powered automation that saves time, reduces costs, and helps you
            grow.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {WHY.map((w) => {
            const Ico = w.icon;
            return (
              <div
                key={w.title}
                className="rounded-2xl border border-gray-100 bg-white p-4"
              >
                <div className="h-10 w-10 rounded-xl bg-[#F3EFFC] flex items-center justify-center">
                  <Ico className="h-5 w-5 text-[#4B2AAD]" />
                </div>
                <p className="mt-3 font-display text-sm font-bold text-gray-900">
                  {w.title}
                </p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  {w.desc}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Request Demo CTA */}
      <section id="request-demo" className="dyntra-container pb-14">
        <div className="rounded-3xl bg-gradient-to-br from-[#4B2AAD] to-[#6B4CD9] text-white p-6 sm:p-10 grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-6 items-center">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl font-extrabold">
              Ready to Transform Your Clinic?
            </h3>
            <p className="text-sm opacity-90 mt-2 max-w-md">
              Request a personalized demo and see how Dyntra Connect can transform
              your clinic operations — completely free for Dyntra Clinics
              franchise partners.
            </p>
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              <Perk label="No Setup Fee" />
              <Perk label="No Monthly Fee" />
              <Perk label="100% Free" />
            </div>
          </div>

          <form
            onSubmit={submit}
            className="rounded-2xl bg-white/95 backdrop-blur text-gray-900 p-5 grid grid-cols-1 sm:grid-cols-2 gap-3"
          >
            <Input label="Full Name" placeholder="Enter your full name" value={form.full_name} onChange={(v) => set("full_name", v)} testid="dc-name" />
            <Input label="Mobile Number" placeholder="+91 XXXXXXXXXX" value={form.mobile} onChange={(v) => set("mobile", v)} testid="dc-mobile" />
            <Input label="Clinic / Hospital Name" placeholder="Enter clinic or hospital name" value={form.clinic} onChange={(v) => set("clinic", v)} testid="dc-clinic" />
            <Input label="Email Address" placeholder="Enter your email address" type="email" value={form.email} onChange={(v) => set("email", v)} testid="dc-email" />
            <Input label="City" placeholder="Enter your city" value={form.city} onChange={(v) => set("city", v)} testid="dc-city" />
            <Input label="How many chairs in your clinic?" placeholder="e.g. 3" value={form.chairs} onChange={(v) => set("chairs", v)} testid="dc-chairs" />
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="dc-submit"
                className="w-full dyntra-btn-primary py-3"
              >
                {status === "loading" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Request a Free Demo"
                )}
              </button>
              {status === "success" && (
                <p className="mt-2 text-xs text-emerald-600 font-semibold text-center">
                  Request received — our team will call you shortly.
                </p>
              )}
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}

function MetricPill({ label, value, up }) {
  return (
    <div className="rounded-xl bg-white/15 p-2">
      <p className="text-[10px] opacity-80">{label}</p>
      <p className="text-lg font-bold">{value}</p>
      {up && <p className="text-[9px] text-emerald-300">{up}</p>}
    </div>
  );
}

function FeeCard({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/70 border border-white/70 p-3">
      <p className="font-display text-2xl font-extrabold text-[#4B2AAD]">{value}</p>
      <p className="text-[11px] text-gray-600 font-semibold uppercase tracking-wider">
        {label}
      </p>
    </div>
  );
}

function Perk({ label }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-white/15 border border-white/30 px-3 py-1 backdrop-blur-sm">
      <ShieldCheck className="h-3 w-3" /> {label}
    </span>
  );
}

function Input({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm"
      />
    </div>
  );
}
